import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Loader from 'react-loader-spinner';
import { FiTrash2, FiUpload, FiLink, FiImage, FiExternalLink } from 'react-icons/fi';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { useDispatch } from 'react-redux';
import '../styles/manageMatchMedia.css';

const PHOTOS_LIMIT = 24;

function ManageMatchMedia(props) {
  const dispatch = useDispatch();
  const matchId = props.match?.params?.matchId;

  const [loading, setLoading] = useState(false);
  const [savingVideos, setSavingVideos] = useState(false);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const [vkLink, setVkLink] = useState('');
  const [vkAlbumTitle, setVkAlbumTitle] = useState('');
  const [vkPhotos, setVkPhotos] = useState([]);
  const [loadingVk, setLoadingVk] = useState(false);
  const [uploadingVk, setUploadingVk] = useState(false);

  const [href1, setHref1] = useState('');
  const [href2, setHref2] = useState('');
  const [href3, setHref3] = useState('');

  const [photos, setPhotos] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMorePhotos, setHasMorePhotos] = useState(false);
  const [totalPhotos, setTotalPhotos] = useState(null);

  const [message, setMessage] = useState({ text: '', type: '' });

  const extractVideos = (payload) => {
    if (Array.isArray(payload?.video)) {
      return payload.video;
    }

    if (Array.isArray(payload?.data?.video)) {
      return payload.data.video;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    return [];
  };

  const getVideoByType = (items, type) => {
    const videoItem = items.find((item) => Number(item?.type) === Number(type));
    return videoItem?.video || '';
  };

  const extractPhotosPayload = (payload) => {
    const body = payload?.data ? payload.data : payload;

    const list = Array.isArray(body)
      ? body
      : Array.isArray(body?.photos)
        ? body.photos
        : Array.isArray(body?.data)
          ? body.data
          : [];

    const cursor = body?.nextCursor ?? body?.next_cursor ?? body?.cursor ?? null;
    const hasMore = body?.hasNext ?? body?.hasMore ?? body?.has_more ?? !!cursor;
    const total = body?.total ?? null;

    return {
      list,
      cursor,
      hasMore,
      total,
    };
  };

  const getPhotoId = (photo) => {
    return photo?.photo_id || photo?.id || photo?.media_id || photo?.match_photo_id;
  };

  const getPhotoUrl = (photo) => {
    const raw = photo?.photo || photo?.href || photo?.url || photo?.image || photo?.path;
    if (!raw) {
      return '';
    }

    if (String(raw).startsWith('http')) {
      return raw;
    }

    if (String(raw).startsWith('/')) {
      return BASE_URL.url + raw;
    }

    return `${BASE_URL.url}/${raw}`;
  };

  const getAuthHeaders = async () => {
    const isTokenExpired = checkIfTokenExpired();
    if (isTokenExpired) {
      await dispatch(refreshTokens());
    }

    const tokens = getTokensFromStore();
    return {
      Authorization: 'Bearer ' + tokens.access_token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  };

  const loadVideos = async () => {
    const headers = await getAuthHeaders();
    const response = await axios.get(BASE_URL.url + `/matches/${matchId}/media`, { headers });

    const videos = extractVideos(response.data);
    setHref1(getVideoByType(videos, 1));
    setHref2(getVideoByType(videos, 2));
    setHref3(getVideoByType(videos, 3));
  };

  const loadPhotos = async (append = false) => {
    setLoadingPhotos(true);
    try {
      const headers = await getAuthHeaders();
      const payload = {
        limit: PHOTOS_LIMIT,
      };

      if (append && nextCursor) {
        payload.nextCursor = nextCursor;
      }

      const response = await axios.post(BASE_URL.url + `/matches/${matchId}/photos`, payload, { headers });
      const parsed = extractPhotosPayload(response.data);

      setPhotos((prev) => {
        const nextList = append ? [...prev, ...parsed.list] : parsed.list;
        const uniqueById = [];
        const used = new Set();
        nextList.forEach((item) => {
          const id = String(getPhotoId(item) || Math.random());
          if (!used.has(id)) {
            used.add(id);
            uniqueById.push(item);
          }
        });
        return uniqueById;
      });

      setNextCursor(parsed.cursor);
      setHasMorePhotos(!!parsed.hasMore);
      if (parsed.total !== null) {
        setTotalPhotos(parsed.total);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
      setMessage({ text: 'Не удалось загрузить фотографии матча', type: 'error' });
    } finally {
      setLoadingPhotos(false);
    }
  };

  const loadPageData = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      await Promise.all([loadVideos(), loadPhotos(false)]);
    } catch (error) {
      console.error('Error loading match media page:', error);
      setMessage({ text: 'Не удалось загрузить данные медиа матча', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (matchId) {
      loadPageData();
    }
  }, [matchId]);

  const handleSaveVideos = async () => {
    if (!matchId) {
      return;
    }

    setSavingVideos(true);
    setMessage({ text: '', type: '' });
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        BASE_URL.url + '/matches/setMatchVideos',
        {
          match_id: matchId,
          href1,
          href2,
          href3,
        },
        { headers }
      );

      setMessage({ text: 'Видео успешно сохранены', type: 'success' });
    } catch (error) {
      console.error('Error saving videos:', error);
      setMessage({ text: 'Не удалось сохранить ссылки на видео', type: 'error' });
    } finally {
      setSavingVideos(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!photoId) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      await axios.post(
        BASE_URL.url + `/matches/${matchId}/deletePhotos`,
        {
          photo_id: photoId,
        },
        { headers }
      );

      setPhotos((prev) => prev.filter((item) => String(getPhotoId(item)) !== String(photoId)));
      setMessage({ text: 'Фото удалено', type: 'success' });
    } catch (error) {
      console.error('Error deleting photo:', error);
      setMessage({ text: 'Не удалось удалить фото', type: 'error' });
    }
  };

  const handleUploadPhotos = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }

    setUploadingPhotos(true);
    setMessage({ text: '', type: '' });

    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const form = new FormData();
      form.append('match_id', String(matchId));
      files.forEach((file) => {
        form.append('photoes', file, file.name);
      });

      await axios.post(BASE_URL.url + '/matches/setMatchPhotoes', form, {
        headers: {
          Authorization: 'Bearer ' + tokens.access_token,
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        }
      });

      await loadPhotos(false);
      setMessage({ text: 'Фото успешно добавлены', type: 'success' });
    } catch (error) {
      console.error('Error uploading photos:', error);
      setMessage({ text: 'Не удалось загрузить фото', type: 'error' });
    } finally {
      setUploadingPhotos(false);
      event.target.value = '';
    }
  };

  const handleLoadVkAlbum = async () => {
    if (!vkLink.trim()) return;
    setLoadingVk(true);
    setVkAlbumTitle('');
    setVkPhotos([]);
    setMessage({ text: '', type: '' });
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + '/matches/getVkPhotoes/',
        { link: vkLink.trim(), match_id: matchId },
        { headers }
      );
      const data = response.data?.data || response.data || {};
      setVkAlbumTitle(data.albumTitle || '');
      setVkPhotos(Array.isArray(data.photos) ? data.photos : []);
    } catch (error) {
      console.error('Error loading VK album:', error);
      setMessage({ text: 'Не удалось загрузить альбом ВК', type: 'error' });
    } finally {
      setLoadingVk(false);
    }
  };

  const handleUploadVkPhotos = async () => {
    if (vkPhotos.length === 0) return;
    setUploadingVk(true);
    setMessage({ text: '', type: '' });
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const form = new FormData();
      form.append('match_id', String(matchId));

      // Fetch each VK photo URL and append as blob
      const blobs = await Promise.all(
        vkPhotos.map(async (url) => {
          const res = await fetch(url);
          const blob = await res.blob();
          const ext = blob.type.split('/')[1] || 'jpg';
          return { blob, ext };
        })
      );

      blobs.forEach(({ blob, ext }, i) => {
        form.append('photoes', blob, `vk_photo_${i + 1}.${ext}`);
      });

      await axios.post(BASE_URL.url + '/matches/setMatchPhotoes', form, {
        headers: {
          Authorization: 'Bearer ' + tokens.access_token,
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      });

      setVkPhotos([]);
      setVkLink('');
      setVkAlbumTitle('');
      await loadPhotos(false);
      setMessage({ text: 'Фото из ВК успешно добавлены к матчу', type: 'success' });
    } catch (error) {
      console.error('Error uploading VK photos:', error);
      setMessage({ text: 'Не удалось добавить фото из ВК', type: 'error' });
    } finally {
      setUploadingVk(false);
    }
  };

  const mediaSummary = useMemo(() => {
    return {
      videos: [href1, href2, href3].filter((item) => !!item).length,
      photos: photos.length,
    };
  }, [href1, href2, href3, photos]);

  return (
    <div className="layoutWrapper">
      <div className="matchCenterWrapper match-media-page">
        <div className="matchCenterHeader">
          <div className="match-media-header-row">
            <div>
              <span className="matchCenterName">Медиа матча #{matchId}</span>
              <p>Видео: {mediaSummary.videos} из 3, фото: {mediaSummary.photos}{totalPhotos !== null ? ` из ${totalPhotos}` : ''}</p>
            </div>
            <button className="match-media-back-btn" onClick={() => props.history.push(`/manage_match/${matchId}`)}>
              Назад к матчу
            </button>
          </div>
        </div>

        <div className="match-media-content">
          {message.text && (
            <div className={`match-media-message ${message.type === 'success' ? 'success' : 'error'}`}>
              {message.text}
            </div>
          )}

          {loading ? (
            <div className="match-media-loading">
              <Loader type="Rings" color="#04a9f5" height={45} width={45} />
              <span>Загрузка медиа...</span>
            </div>
          ) : (
            <>
              <div className="match-media-section">
                <div className="match-media-section-header">
                  <h3><FiLink /> Видео</h3>
                  <button
                    className="match-media-save-btn"
                    onClick={handleSaveVideos}
                    disabled={savingVideos}
                  >
                    {savingVideos ? 'Сохранение...' : 'Сохранить видео'}
                  </button>
                </div>

                <div className="match-media-videos-grid">
                  <div className="match-media-input-group">
                    <label>1. Трансляция</label>
                    <input
                      type="url"
                      value={href1}
                      onChange={(e) => setHref1(e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="match-media-input-group">
                    <label>2. Обзор</label>
                    <input
                      type="url"
                      value={href2}
                      onChange={(e) => setHref2(e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="match-media-input-group">
                    <label>3. Дополнительно</label>
                    <input
                      type="url"
                      value={href3}
                      onChange={(e) => setHref3(e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              <div className="match-media-section">
                <div className="match-media-section-header">
                  <h3><FiExternalLink /> Импорт из ВКонтакте</h3>
                </div>
                <div className="match-media-vk-row">
                  <input
                    type="url"
                    className="match-media-vk-input"
                    value={vkLink}
                    onChange={(e) => setVkLink(e.target.value)}
                    placeholder="Ссылка на альбом ВК..."
                    onKeyDown={(e) => e.key === 'Enter' && handleLoadVkAlbum()}
                  />
                  <button
                    className="match-media-save-btn"
                    onClick={handleLoadVkAlbum}
                    disabled={loadingVk || !vkLink.trim()}
                  >
                    {loadingVk ? 'Загрузка...' : 'Загрузить'}
                  </button>
                </div>

                {loadingVk && (
                  <div className="match-media-loading-inline">Загрузка альбома...</div>
                )}

                {!loadingVk && vkPhotos.length > 0 && (
                  <>
                    <div className="match-media-vk-album-header">
                      {vkAlbumTitle ? (
                        <p className="match-media-vk-album-title">{vkAlbumTitle} — {vkPhotos.length} фото</p>
                      ) : (
                        <p className="match-media-vk-album-title">{vkPhotos.length} фото</p>
                      )}
                      <button
                        className="match-media-save-btn"
                        onClick={handleUploadVkPhotos}
                        disabled={uploadingVk}
                      >
                        {uploadingVk ? 'Добавление...' : 'Добавить к матчу'}
                      </button>
                    </div>
                    <div className="match-media-photos-grid">
                      {vkPhotos.map((url, i) => (
                        <div key={url + i} className="match-media-photo-card">
                          <img src={url} alt={`vk-${i}`} />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="match-media-section">
                <div className="match-media-section-header">
                  <h3><FiImage /> Фотографии</h3>
                  <label className={`match-media-upload-btn ${uploadingPhotos ? 'disabled' : ''}`}>
                    <FiUpload /> {uploadingPhotos ? 'Загрузка...' : 'Добавить фото'}
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleUploadPhotos}
                      disabled={uploadingPhotos}
                    />
                  </label>
                </div>

                {loadingPhotos && photos.length === 0 ? (
                  <div className="match-media-loading-inline">Загрузка фото...</div>
                ) : photos.length > 0 ? (
                  <div className="match-media-photos-grid">
                    {photos.map((photo) => {
                      const photoId = getPhotoId(photo);
                      const photoUrl = getPhotoUrl(photo);

                      return (
                        <div key={photoId || photoUrl} className="match-media-photo-card">
                          <img src={photoUrl} alt="Match" />
                          <button
                            type="button"
                            className="match-media-delete-btn"
                            onClick={() => handleDeletePhoto(photoId)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="match-media-empty">Фотографий пока нет</div>
                )}

                {hasMorePhotos && (
                  <button
                    className="match-media-load-more-btn"
                    onClick={() => loadPhotos(true)}
                    disabled={loadingPhotos}
                  >
                    {loadingPhotos ? 'Загрузка...' : 'Загрузить еще'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManageMatchMedia;